import Spinner from '@viking-eng/icon/lib/Spinner';
import PropTypes from '@viking-eng/prop-types';
import { getAccessibilityFunctions } from '@viking-eng/utils';
import classNames from 'classnames';
import React from 'react';
import './Button.css';

const ContentWrapper = ({ children, loading }) => {
  if (!children) {
    return null;
  }
  if (loading) {
    return <span className="button-content">{children}</span>;
  }
  return children;
};

ContentWrapper.propTypes = {
  loading: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.element, PropTypes.jsx]),
};

/*
  Button component for buttons
  attributes: pass additional attributes to be placed on the button
  appearance: one of icon, link, or primary
  children: text or React component to rendered inside the button
  disabled: is a disabled button
  loading: is a loading state for the button
  inlineCss: is additional css that can be passed to the Button
  onButtonClick: determines what happens when a button is clicked
*/
function Button({
  appearance,
  ariaLabel,
  attributes,
  children,
  className,
  dataId,
  disabled,
  id,
  inlineCss,
  loading,
  onButtonClick,
  size,
}) {
  // If Empty

  let additionalClass = '';

  if (appearance) {
    const appearanceComp = appearance.toLowerCase();
    switch (appearanceComp) {
      case 'border':
      case 'clear':
      case 'dropdown':
      case 'dropdown disabled':
      case 'icon':
      case 'link':
      case 'primary':
      case 'secondary-blue':
      case 'secondary-gray':
      case 'strong-border':
      case 'text-with-icon':
      case 'text-with-icon-left':
        additionalClass = `btn-${appearanceComp}`;
        break;
      default:
        break;
    }
  }

  const classes = classNames(
    'viking-button btn',
    className,
    {
      [`${additionalClass}`]: additionalClass,
      loading,
    },
    size ? `btn-${size}` : ''
  );

  // the "z" is needed because attribute ordering
  const zButtonAttributes = {
    ...attributes,
    ...getAccessibilityFunctions(),
  };

  return (
    <button
      aria-busy={loading}
      aria-label={ariaLabel}
      type="button"
      className={classes}
      data-id={dataId}
      disabled={disabled}
      id={id}
      onClick={loading ? () => false : onButtonClick}
      style={inlineCss}
      {...zButtonAttributes}
    >
      <ContentWrapper loading={loading}>{children}</ContentWrapper>
      <span className="loading-icon">
        <Spinner />
      </span>
    </button>
  );
}

Button.propTypes = {
  appearance: PropTypes.string,
  ariaLabel: PropTypes.string,
  attributes: PropTypes.shape({}),
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.element, PropTypes.jsx]),
  className: PropTypes.string,
  dataId: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  inlineCss: PropTypes.objectOf(PropTypes.string),
  loading: PropTypes.bool,
  onButtonClick: PropTypes.func,
  size: PropTypes.oneOf(['sm']),
};

Button.defaultProps = {
  appearance: '',
  attributes: {},
  children: undefined,
  className: '',
  dataId: undefined,
  disabled: false,
  inlineCss: {},
  loading: false,
  onButtonClick: () => true,
};

export default Button;
